// https://ui.shadcn.com/docs/components/tabs
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/utils/tw-helpers'

const tabsVariants = cva('inline-flex justify-center', {
  variants: {
    variant: {
      default: '',
      plain: '',
      plans: '',
      curriculum: '',
      'school-admin': '',
      'avatar-parts': '',
      'avatar-sub-parts': '',
    },
  },
})

interface ITabs extends VariantProps<typeof tabsVariants> {}

const TabsContext = React.createContext<ITabs>({
  variant: 'default',
})

export interface TabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  variant?: ITabs['variant']
}

const Tabs = ({ children, variant, ...props }: TabsProps) => (
  <TabsContext.Provider value={{ variant }}>
    <TabsPrimitive.Root {...props}>{children}</TabsPrimitive.Root>
  </TabsContext.Provider>
)

const tabsListVariants = cva('flex', {
  variants: {
    variant: {
      default: '',
      plain: 'gap-[6px]',
      plans: 'flex gap-2',
      curriculum: 'grid grid-cols-2 gap-[6px] rounded-md',
      'school-admin': cn('flex justify-center gap-x-4'),
      'avatar-parts':
        'h-[44px] lg:h-[67px] bg-secondary-95 rounded-tl-[11px] rounded-tr-[11px] lg:rounded-tl-[14px] lg:rounded-tr-[14px]',
      'avatar-sub-parts': '-mb-0.5',
    },
  },
})

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext)

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant, className }))}
      {...props}
    />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap leading-[normal] ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-foreground',
  {
    variants: {
      variant: {
        default: 'rounded-md px-3 py-1 text-sm font-medium data-[state=active]:shadow',
        plain:
          'h-[34px] bg-[#5166C7] rounded-tl-md rounded-tr-md flex-1 py-2 text-[13px] text-white font-medium leading-[normal] data-[state=active]:bg-white data-[state=active]:text-[#222222] active:bg-[#495CB3]',
        plans: cn(
          'h-[44px] flex-1 rounded-[22px] bg-basic-bg text-xs text-[#5166C7] shadow-tiny',
          'data-[state=active]:bg-[#5166C7] data-[state=active]:text-white data-[state=active]:shadow-none'
        ),
        curriculum: cn(
          'p-1 whitespace-break-spaces text-[10px] text-secondary-text h-[34px] px-[6px] rounded-md bg-basic-bg shadow-tiny leading-[12px]',
          'data-[state=active]:bg-primary-20 data-[state=active]:text-primary-100 data-[state=active]:shadow-none'
        ),
        'school-admin': cn(
          'font-bold text-[17px] h-[48px] w-[206px] rounded-full p-6 bg-basic-bg text-primary-100 shadow-tiny',
          'data-[state=active]:bg-primary-100 data-[state=active]:text-white data-[state=active]:shadow-none'
        ),
        'avatar-parts': cn(
          'text-secondary-60 rounded-tl-[11px] rounded-tr-[11px] w-[55px] lg:w-[71px] shrink-0 lg:rounded-tl-[14px] lg:rounded-tr-[14px] ',
          'data-[state=active]:text-primary-40',
          'active:bg-secondary-90 active:text-secondary-50'
        ),
        'avatar-sub-parts': cn(
          'px-[14px] pt-[11px] pb-[6px] text-secondary-60 font-bold text-[14px] border-b-[5px] border-b-transparent',
          'data-[state=active]:text-primary-40 data-[state=active]:border-b-primary-60'
        ),
      },
    },
  }
)

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext)

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant, className }))}
      {...props}
    />
  )
})

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const tabsContentVariants = cva(
  'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'mt-2',
        plain: 'bg-white p-4 rounded-bl-md rounded-br-md',
        plans: '',
        curriculum: 'pt-5',
        'school-admin': '',
        'avatar-parts': cn('h-full', 'active:text-primary-60'),
        'avatar-sub-parts': cn(
          'flex gap-[10px] flex-wrap lg:gap-[14px]',
          'data-[state=active]:pt-4 lg:data-[state=active]:pt-[18px]'
        ),
      },
    },
  }
)

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext)

  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(tabsContentVariants({ variant, className }))}
      {...props}
    />
  )
})

TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
